/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Case, Switch } from "react-if";
import {
  CostAnalysisChartHeader,
  useCostAnalysis,
  usePeriod,
} from "~/components/Charts/CostAnalysis";
import { ChartType, DateRange } from "~/components/Charts/CostAnalysis/types";
import { getFirstItem } from "~/tools";
import { chartTypeOptions } from "./types";
import {
  OrgUnitAccumulatedCostView,
  OrgUnitCostPerApplicationView,
  OrgUnitCostVersusBudgetView,
} from "./views";
import { OrgUnitCostPerCategoriesView } from "./views/OrgUnitCostPerCategoryView";

type Props = {
  orgUnitId: string;
  applicationIds: string[];
  forecast: number;
};

export default function OrgUnitCostAnalysisSection({
  orgUnitId,
  applicationIds,
  forecast,
}: Props) {
  const [range, setRange] = useState<DateRange | undefined>(undefined);

  const {
    period,
    setPeriod,
    granularity,
    setGranularity,
    isDailyChartViewDisabled,
    startDate,
    endDate,
  } = usePeriod(range);

  const { chartType, setChartType, isChartVisible, showCostTable, hasFooter } =
    useCostAnalysis({
      period,
      chartOptions: chartTypeOptions,
    });

  return (
    <>
      <CostAnalysisChartHeader
        period={period}
        options={chartTypeOptions}
        chartType={chartType}
        range={range}
        granularity={granularity}
        disableDailyView={isDailyChartViewDisabled}
        onHandleClick={setPeriod}
        onSetRange={setRange}
        onSetGranularity={setGranularity}
        onChange={(item) => {
          setChartType(item ?? getFirstItem(chartTypeOptions));
        }}
      />
      <Switch>
        <Case condition={isChartVisible(ChartType.ACCUMULATED_COST)}>
          <OrgUnitAccumulatedCostView
            id={orgUnitId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_VS_BUDGET)}>
          <OrgUnitCostVersusBudgetView
            orgUnitId={orgUnitId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
            forecast={forecast}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_PER_APPLICATION)}>
          <OrgUnitCostPerApplicationView
            id={orgUnitId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_PER_CATEGORY)}>
          <OrgUnitCostPerCategoriesView
            orgUnitId={orgUnitId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
            applicationIds={applicationIds}
          />
        </Case>
      </Switch>
    </>
  );
}
